import { useQuery } from '@tanstack/react-query'

import { apiClient } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'

export interface Subscriber {
  name: string
  active: boolean
  display_name: string
}

const SYSTEMS_QUERY_KEY = ['systems'] as const
const SYSTEMS_STALE_TIME = 5 * 60 * 1000

function sortSubscribers(subscribers: Subscriber[]): Subscriber[] {
  return [...subscribers].sort((a, b) => {
    const nameA = (a.display_name || a.name).toLowerCase()
    const nameB = (b.display_name || b.name).toLowerCase()
    return nameA.localeCompare(nameB)
  })
}

async function fetchSystems(): Promise<Subscriber[]> {
  const response = await apiClient.get<{ systems?: Subscriber[]; total: number }>(
    '/core/v5/systems'
  )
  return sortSubscribers(response.systems ?? [])
}

export function useSystems(enabled = true) {
  return useQuery({
    queryKey: SYSTEMS_QUERY_KEY,
    queryFn: fetchSystems,
    enabled,
    staleTime: SYSTEMS_STALE_TIME,
  })
}

/**
 * Resolves the selected system's friendly display name, falling back to the
 * machine name when the systems list hasn't loaded or lacks a display name.
 * Returns an empty string when no system is selected.
 */
export function useSelectedSystemLabel(): string {
  const { selectedSystem } = useAuthStore()
  const { data: subscribers = [] } = useSystems(!!selectedSystem)
  return subscribers.find((s) => s.name === selectedSystem)?.display_name || selectedSystem || ''
}
